import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, TextInput, Image, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { LiveVisitMap, LiveVisitMapPosition } from '../LiveVisitMap';
import { ContactQuickActions } from '../ContactQuickActions';
import { BusinessUserPicker } from '../BusinessUserPicker';
import { apiService, Visit, VisitOutcome, VisitPhoto } from '../../services/api';
import { useAppContext } from '../../contexts/AppContext';
import { useAppStore } from '../../store/appStore';
import { useLocale } from '../../contexts/LocaleContext';
import { useThemedRTLStyles } from '../../hooks/useThemedRTLStyles';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { colors, spacing, typography } from '../../theme';
import { getImageUrlSync } from '../../utils/imageProxy';
import { CapturedPosition, captureVisitPhoto, getCurrentPosition, haversineMeters } from '../../utils/visitCapture';
import { useLiveDirections } from '../../hooks/useLiveDirections';

interface EffectiveConfig {
  outcomes: VisitOutcome[];
  enforcement: {
    location: { enabled: boolean; radiusMeters: number; maxAccuracyMeters: number; allowOverrideWithReason: boolean };
    photos: { requireOnStart: boolean; requireOnEnd: boolean; cameraOnly: boolean; minPhotosOnStart: number; minPhotosOnEnd: number; maxPhotos: number };
  };
}

interface Props {
  visitId: string;
  contactId: string;
  contactName: string;
  phoneNumber?: string;
  /** `ended` = closed because the visit was just ended. */
  onClose: (ended?: boolean) => void;
  /** Visit / contact data changed (started, ended, note added…) — parent reloads. */
  onChanged: () => void;
  /** Each receives this visit's id so the created record is logged on it. */
  onAddOrder: (visitId: string) => void;
  onAddQuotation: (visitId: string) => void;
  onAddVendor: (visitId: string) => void;
  /** Vendor already linked to this contact — "Add as vendor" becomes "View vendor". */
  linkedVendor?: { id: string; name: string } | null;
}

const formatDateTime = (value?: string | Date) =>
  value ? new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const ACTIVITY_ICON: Record<string, keyof typeof Ionicons.glyphMap> = {
  order: 'cart-outline',
  quotation: 'document-text-outline',
  vendor: 'storefront-outline',
  reminder: 'alarm-outline',
  list: 'list-outline',
  assignment: 'person-add-outline',
  note: 'create-outline',
};
const formatMeters = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

/**
 * One visit, worked end-to-end inside the contact profile:
 * view → directions (in-app DISLIX map, server OSRM route) → the visit's
 * rules shown up front → start (location check-in + start photos) → actions
 * while there (note, reminder, list, assignment, quotation, order, add as
 * vendor) → End visit with the outcome as the main action.
 * Uses the same server endpoints/enforcement as VisitExecuteScreen.
 */
export const ContactVisitWorkspace: React.FC<Props> = ({
  visitId, contactId, contactName, phoneNumber, onClose, onChanged, onAddOrder, onAddQuotation, onAddVendor, linkedVendor,
}) => {
  const s = useThemedRTLStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const { showNotification, navigateTo } = useAppContext();
  const { currentBusiness, user } = useAppStore();

  const [visit, setVisit] = useState<Visit | null>(null);
  const [config, setConfig] = useState<EffectiveConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState<CapturedPosition | null>(null);
  const [locating, setLocating] = useState(false);
  const [startPhotos, setStartPhotos] = useState<VisitPhoto[]>([]);
  const [endPhotos, setEndPhotos] = useState<VisitPhoto[]>([]);
  const [capturing, setCapturing] = useState<'start' | 'end' | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [geofenceError, setGeofenceError] = useState<{ distanceMeters: number; radiusMeters: number } | null>(null);
  const [ongoingVisitId, setOngoingVisitId] = useState<string | null>(null);
  const [busy, setBusy] = useState<'start' | 'end' | 'note' | null>(null);
  const [noteText, setNoteText] = useState('');
  const [showOutcomes, setShowOutcomes] = useState(false);
  const [outcome, setOutcome] = useState<VisitOutcome | null>(null);
  const [rescheduleHours, setRescheduleHours] = useState('');
  const [reminderMinutes, setReminderMinutes] = useState('');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [assignSource, setAssignSource] = useState<'users' | 'departments'>('users');
  const [assignee, setAssignee] = useState<string | undefined>(undefined);
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [v, c] = await Promise.all([apiService.getVisit(visitId), apiService.getVisitEffectiveConfig(visitId).catch(() => null)]);
      if (v?.statusCode === 1000 && v.data) setVisit(v.data);
      if (c?.statusCode === 1000 && c.data) setConfig(c.data);
    } catch (err: any) {
      showNotification('error', t('visits.detail.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [visitId, showNotification, t]);

  useEffect(() => {
    setLoading(true);
    setStartPhotos([]);
    setEndPhotos([]);
    setShowOutcomes(false);
    setOutcome(null);
    load();
  }, [load]);

  const target = visit?.targetLocation;
  const hasTarget = typeof target?.latitude === 'number' && typeof target?.longitude === 'number';
  const loc = config?.enforcement?.location;
  const photos = config?.enforcement?.photos;
  const distance = position && hasTarget ? haversineMeters(position.latitude, position.longitude, target!.latitude!, target!.longitude!) : null;
  const withinRadius = !loc?.enabled || (distance !== null && distance <= (loc?.radiusMeters ?? Infinity));
  const status = visit?.status;
  const isActive = status === 'in_progress';
  const isStartable = status === 'pending' || status === 'scheduled' || status === 'rescheduled';
  const isMine = !visit?.assignedTo || visit.assignedTo === (user as any)?.id;

  const locate = async () => {
    setLocating(true);
    try {
      setPosition(await getCurrentPosition());
    } catch (err: any) {
      showNotification('error', t('visits.execute.title'), err?.message || t('visits.execute.errors.locationFailed'));
    } finally {
      setLocating(false);
    }
  };

  // Location is needed for the check-in and the distance readout.
  useEffect(() => {
    if (visit && (isStartable || isActive) && loc?.enabled && !position) locate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?._id, loc?.enabled]);

  const takePhoto = async (phase: 'start' | 'end') => {
    const list = phase === 'start' ? startPhotos : endPhotos;
    if (photos && list.length >= photos.maxPhotos) return;
    setCapturing(phase);
    try {
      const photo = await captureVisitPhoto({ phase, cameraOnly: photos?.cameraOnly ?? true, businessId: currentBusiness?._id || '', position });
      if (photo) (phase === 'start' ? setStartPhotos : setEndPhotos)((prev) => [...prev, photo]);
    } catch (err: any) {
      showNotification('error', t('visits.execute.title'), err?.message || t('visits.execute.errors.photoUploadFailed'));
    } finally {
      setCapturing(null);
    }
  };

  // Live position + road route while the directions map is open (follows
  // the agent); the latest live fix also feeds the check-in distance.
  const liveDirections = useLiveDirections(visitId, mapOpen, position);
  useEffect(() => {
    if (liveDirections.position) setPosition(liveDirections.position);
  }, [liveDirections.position]);

  const openDirections = () => setMapOpen(true);

  const startNeedsPhotos = !!photos?.requireOnStart && startPhotos.length < (photos?.minPhotosOnStart || 1);
  const startNeedsLocation = !!loc?.enabled && !position;
  const startNeedsReason = !!loc?.enabled && !!position && !withinRadius && !!loc?.allowOverrideWithReason && !overrideReason.trim();
  const canStart = isStartable && isMine && !startNeedsPhotos && !startNeedsLocation && !startNeedsReason;

  const handleStart = async () => {
    setGeofenceError(null);
    setBusy('start');
    try {
      const payload: Record<string, any> = {
        latitude: position?.latitude,
        longitude: position?.longitude,
        accuracyMeters: position?.accuracyMeters,
        photos: startPhotos,
      };
      if (overrideReason.trim()) payload.overrideReason = overrideReason.trim();
      await apiService.startVisit(visitId, payload);
      await load();
      onChanged();
    } catch (err: any) {
      const geofence = err?.data?.additionalInfo?.geofence;
      const ongoing = err?.data?.additionalInfo?.ongoingVisitId;
      if (geofence && typeof geofence.distanceMeters === 'number') setGeofenceError({ distanceMeters: geofence.distanceMeters, radiusMeters: geofence.radiusMeters });
      else if (ongoing) setOngoingVisitId(ongoing);
      else showNotification('error', t('visits.execute.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setBusy(null);
    }
  };

  // One notes list: a note taken during the visit goes to the contact's
  // notes (what the profile shows), tagged with the visit, and is also kept
  // on the visit record for reporting.
  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setBusy('note');
    try {
      // The server also writes it to the contact's notes (tagged [Visit]).
      await apiService.addVisitNote(visitId, text);
      setNoteText('');
      onChanged();
      showNotification('success', t('visitWorkspace.title'), t('visitWorkspace.noteAdded'));
    } catch (err: any) {
      showNotification('error', t('visitWorkspace.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setBusy(null);
    }
  };

  const logActivity = async (type: 'reminder' | 'list' | 'assignment') => {
    try {
      await apiService.addVisitActivity(visitId, { type });
      const v = await apiService.getVisit(visitId);
      if (v?.statusCode === 1000 && v.data) setVisit(v.data);
    } catch {
      // Logging only — the action itself already succeeded.
    }
    onChanged();
  };

  const pickOutcome = (o: VisitOutcome) => {
    setOutcome(o);
    setRescheduleHours(String(o.action?.config?.defaultOffsetHours ?? ''));
    setReminderMinutes(String(o.action?.config?.defaultOffsetMinutes ?? ''));
    setOutcomeNote('');
    setAssignee(undefined);
  };

  const endNeedsPhotos = !!photos?.requireOnEnd && endPhotos.length < (photos?.minPhotosOnEnd || 1);

  const handleEnd = async () => {
    if (!outcome) return;
    let actionPayload: Record<string, any> | undefined;
    let note: string | undefined;
    const type = outcome.action?.type;
    if (type === 'reschedule') actionPayload = { offsetHours: Number(rescheduleHours) || 0 };
    else if (type === 'reminder') actionPayload = { offsetMinutes: Number(reminderMinutes) || 0 };
    else if (type === 'assign') {
      if (!assignee) return showNotification('warning', t('visitWorkspace.title'), t('visits.execute.errors.assigneeRequired'));
      actionPayload = assignSource === 'users' ? { assignedTo: assignee } : { departmentId: assignee };
    } else if (type === 'note') {
      if (!outcomeNote.trim()) return showNotification('warning', t('visitWorkspace.title'), t('visits.execute.errors.noteRequired'));
      note = outcomeNote.trim();
      actionPayload = { noteText: note };
    }
    if (!note && outcomeNote.trim()) note = outcomeNote.trim();
    setBusy('end');
    try {
      const p = position || (loc?.enabled ? await getCurrentPosition().catch(() => null) : null);
      const payload: Record<string, any> = {
        outcomeId: outcome.id,
        latitude: p?.latitude,
        longitude: p?.longitude,
        accuracyMeters: p?.accuracyMeters,
        photos: endPhotos,
      };
      if (actionPayload) payload.actionPayload = actionPayload;
      if (note) payload.note = note;
      await apiService.endVisit(visitId, payload);
      showNotification('success', t('visitWorkspace.title'), t('visitWorkspace.ended', { outcome: outcome.label }));
      onChanged();
      onClose(true);
    } catch (err: any) {
      showNotification('error', t('visitWorkspace.title'), err?.message || t('visits.errors.generic'));
    } finally {
      setBusy(null);
    }
  };

  const rules = useMemo(() => {
    const out: Array<{ icon: keyof typeof Ionicons.glyphMap; text: string }> = [];
    if (loc?.enabled) {
      out.push({
        icon: 'locate-outline',
        text: loc.allowOverrideWithReason
          ? t('visitWorkspace.rules.radiusOverride', { radius: loc.radiusMeters })
          : t('visitWorkspace.rules.radius', { radius: loc.radiusMeters }),
      });
    }
    if (photos?.requireOnStart) out.push({ icon: 'camera-outline', text: t('visitWorkspace.rules.startPhotos', { count: photos.minPhotosOnStart || 1 }) });
    if (photos?.requireOnEnd) out.push({ icon: 'camera-outline', text: t('visitWorkspace.rules.endPhotos', { count: photos.minPhotosOnEnd || 1 }) });
    if (photos?.cameraOnly && (photos.requireOnStart || photos.requireOnEnd)) out.push({ icon: 'aperture-outline', text: t('visitWorkspace.rules.cameraOnly') });
    if (visit?.expectedDurationMinutes) out.push({ icon: 'time-outline', text: t('visitWorkspace.rules.duration', { minutes: visit.expectedDurationMinutes }) });
    if (visit?.dueBy) out.push({ icon: 'hourglass-outline', text: t('visitWorkspace.rules.dueBy', { time: formatDateTime(visit.dueBy) }) });
    return out;
  }, [loc, photos, visit, t]);

  const photoStrip = (list: VisitPhoto[], phase: 'start' | 'end') => (
    <View style={s.photoRow}>
      {list.map((p, i) => (
        <View key={`${p.mediaId}-${i}`}>
          <Image source={{ uri: getImageUrlSync(p.url, apiService.getBaseURL?.() ?? null) }} style={s.photo} />
          <TouchableOpacity
            style={s.photoRemove}
            onPress={() => (phase === 'start' ? setStartPhotos : setEndPhotos)((prev) => prev.filter((_, j) => j !== i))}
          >
            <Ionicons name="close" size={12} color="#fff" />
          </TouchableOpacity>
        </View>
      ))}
      {(!photos || list.length < photos.maxPhotos) && (
        <TouchableOpacity style={s.photoAdd} onPress={() => takePhoto(phase)} disabled={!!capturing}>
          {capturing === phase ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Ionicons name="camera" size={20} color={colors.primary as string} />}
        </TouchableOpacity>
      )}
    </View>
  );

  if (loading) {
    return (
      <View style={s.card}>
        <ActivityIndicator color={colors.primary as string} />
      </View>
    );
  }
  if (!visit) return null;

  const outcomes = (config?.outcomes || []).filter((o) => o.isActive !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <View style={s.card}>
      {/* 1. Visit */}
      <View style={s.headerRow}>
        <View style={[s.statusDot, { backgroundColor: isActive ? (colors.status.info as string) : (colors.primary as string) }]} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={s.title} numberOfLines={1}>{t('visitWorkspace.title')} · {t(`visits.status.${visit.status}` as any)}</Text>
          <Text style={s.subtitle} numberOfLines={2}>
            {[isActive ? t('visitWorkspace.startedAt', { time: formatDateTime(visit.startedAt) }) : formatDateTime(visit.scheduledFor), target?.address].filter(Boolean).join(' · ')}
          </Text>
        </View>
        <TouchableOpacity onPress={() => navigateTo('visit-detail', undefined, { visitId, record: true })} hitSlop={8} style={s.iconBtn}>
          <Ionicons name="open-outline" size={16} color={colors.text.secondary as string} />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => onClose()} hitSlop={8} style={s.iconBtn}>
          <Ionicons name="close" size={18} color={colors.text.secondary as string} />
        </TouchableOpacity>
      </View>

      {/* 2. Directions + distance */}
      {hasTarget && (isStartable || isActive) && (
        <View style={s.directionsRow}>
          <TouchableOpacity style={s.secondaryBtn} onPress={openDirections}>
            <Ionicons name="navigate" size={16} color={colors.primary as string} />
            <Text style={s.secondaryBtnText}>{t('visitWorkspace.directions')}</Text>
          </TouchableOpacity>
          <View style={{ flex: 1, minWidth: 0 }}>
            {locating ? (
              <Text style={s.muted}>{t('visitWorkspace.locating')}</Text>
            ) : distance !== null ? (
              <Text style={[s.muted, loc?.enabled && !withinRadius && { color: colors.warning as string }]} numberOfLines={2}>
                {t('visitWorkspace.distance', { distance: formatMeters(distance) })}
                {loc?.enabled ? ` · ${withinRadius ? t('visitWorkspace.inRange') : t('visitWorkspace.outOfRange', { radius: loc.radiusMeters })}` : ''}
              </Text>
            ) : (
              <TouchableOpacity onPress={locate}><Text style={s.link}>{t('visitWorkspace.checkLocation')}</Text></TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {/* 3. Rules up front */}
      {rules.length > 0 && (isStartable || isActive) && (
        <View style={s.rules}>
          <Text style={s.sectionLabel}>{t('visitWorkspace.rulesTitle')}</Text>
          {rules.map((r, i) => (
            <View key={i} style={s.ruleRow}>
              <Ionicons name={r.icon} size={14} color={colors.text.secondary as string} />
              <Text style={s.ruleText}>{r.text}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Start */}
      {isStartable && (
        <View style={s.block}>
          {!isMine && <Text style={s.warn}>{t('visitWorkspace.notYours')}</Text>}
          {photos?.requireOnStart && (
            <>
              <Text style={s.sectionLabel}>{t('visits.execute.startPhotos')}</Text>
              {photoStrip(startPhotos, 'start')}
            </>
          )}
          {loc?.enabled && position && !withinRadius && loc.allowOverrideWithReason && (
            <TextInput
              style={s.input}
              value={overrideReason}
              onChangeText={setOverrideReason}
              placeholder={t('visits.execute.overrideReasonPlaceholder')}
              placeholderTextColor={colors.text.disabled as string}
            />
          )}
          {geofenceError && (
            <Text style={s.warn}>
              {t('visitWorkspace.geofenceBlocked', { distance: formatMeters(geofenceError.distanceMeters), radius: geofenceError.radiusMeters })}
            </Text>
          )}
          {ongoingVisitId && (
            <TouchableOpacity onPress={() => navigateTo('visit-execute', undefined, { visitId: ongoingVisitId })}>
              <Text style={s.warn}>{t('visits.execute.ongoingVisitMessage')} <Text style={s.link}>{t('visits.execute.ongoingVisitAction')}</Text></Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity style={[s.primaryBtn, (!canStart || busy === 'start') && s.disabled]} disabled={!canStart || busy === 'start'} onPress={handleStart}>
            {busy === 'start' ? <ActivityIndicator color={colors.text.onPrimary as string} /> : <Ionicons name="play" size={18} color={colors.text.onPrimary as string} />}
            <Text style={s.primaryBtnText}>{t('visitWorkspace.start')}</Text>
          </TouchableOpacity>
          {(startNeedsLocation || startNeedsPhotos || startNeedsReason) && (
            <Text style={s.hint}>
              {startNeedsLocation ? t('visitWorkspace.needLocation') : startNeedsPhotos ? t('visitWorkspace.needStartPhotos') : t('visitWorkspace.needReason')}
            </Text>
          )}
        </View>
      )}

      {/* 4. While at the contact */}
      {isActive && (
        <View style={s.block}>
          <Text style={s.sectionLabel}>{t('visitWorkspace.actionsTitle')}</Text>
          <ContactQuickActions
            contactId={contactId}
            contactName={contactName}
            phoneNumber={phoneNumber}
            onNotify={(type, title, message) => showNotification(type, title, message)}
            onListMembershipChange={() => logActivity('list')}
            onAssignmentChange={() => logActivity('assignment')}
            onReminderCreated={() => logActivity('reminder')}
          />
          <View style={s.actionGrid}>
            {[
              { icon: 'document-text-outline', label: t('visitWorkspace.addQuotation'), onPress: () => onAddQuotation(visitId) },
              { icon: 'cart-outline', label: t('visitWorkspace.addOrder'), onPress: () => onAddOrder(visitId) },
              linkedVendor
                ? { icon: 'storefront', label: t('visitWorkspace.viewVendor'), onPress: () => navigateTo('vendor-detail', linkedVendor.id) }
                : { icon: 'storefront-outline', label: t('visitWorkspace.addVendor'), onPress: () => onAddVendor(visitId) },
            ].map((a) => (
              <TouchableOpacity key={a.label} style={s.actionTile} onPress={a.onPress}>
                <Ionicons name={a.icon as any} size={18} color={colors.primary as string} />
                <Text style={s.actionTileText} numberOfLines={2}>{a.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={s.noteRow}>
            <TextInput
              style={[s.input, { flex: 1, marginBottom: 0 }]}
              value={noteText}
              onChangeText={setNoteText}
              placeholder={t('visitWorkspace.notePlaceholder')}
              placeholderTextColor={colors.text.disabled as string}
              multiline
            />
            <TouchableOpacity style={[s.noteBtn, (!noteText.trim() || busy === 'note') && s.disabled]} disabled={!noteText.trim() || busy === 'note'} onPress={handleAddNote}>
              {busy === 'note' ? <ActivityIndicator size="small" color={colors.text.onPrimary as string} /> : <Ionicons name="send" size={16} color={colors.text.onPrimary as string} />}
            </TouchableOpacity>
          </View>

          {/* 5. End visit — the main action */}
          {!showOutcomes ? (
            <TouchableOpacity style={s.primaryBtn} onPress={() => setShowOutcomes(true)}>
              <Ionicons name="flag" size={18} color={colors.text.onPrimary as string} />
              <Text style={s.primaryBtnText}>{t('visitWorkspace.end')}</Text>
            </TouchableOpacity>
          ) : (
            <View style={s.endSheet}>
              <Text style={s.sectionLabel}>{t('visits.execute.selectOutcome')}</Text>
              <View style={s.outcomeWrap}>
                {outcomes.map((o) => {
                  const selected = outcome?.id === o.id;
                  const c = o.color || (o.resultType === 'success' ? colors.status.success : o.resultType === 'failure' ? colors.status.error : colors.status.info);
                  return (
                    <TouchableOpacity key={o.id} style={[s.outcomeChip, selected && { borderColor: c as string, backgroundColor: `${c as string}1F` }]} onPress={() => pickOutcome(o)}>
                      {o.icon ? <Ionicons name={o.icon as any} size={14} color={c as string} /> : null}
                      <Text style={[s.outcomeText, selected && { color: c as string }]}>{o.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {outcome?.action?.type === 'reschedule' && (
                <TextInput style={s.input} value={rescheduleHours} onChangeText={setRescheduleHours} keyboardType="numeric" placeholder={t('visits.execute.rescheduleHours')} placeholderTextColor={colors.text.disabled as string} />
              )}
              {outcome?.action?.type === 'reminder' && (
                <TextInput style={s.input} value={reminderMinutes} onChangeText={setReminderMinutes} keyboardType="numeric" placeholder={t('visits.execute.reminderMinutes')} placeholderTextColor={colors.text.disabled as string} />
              )}
              {outcome?.action?.type === 'assign' && (
                <View style={s.assignRow}>
                  {(['users', 'departments'] as const).map((src) => (
                    <TouchableOpacity key={src} style={[s.segment, assignSource === src && s.segmentOn]} onPress={() => { setAssignSource(src); setAssignee(undefined); }}>
                      <Text style={[s.segmentText, assignSource === src && s.segmentTextOn]}>{src === 'users' ? t('visits.create.assignToUser') : t('visits.create.assignToDepartment')}</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={s.secondaryBtn} onPress={() => setShowAssigneePicker(true)}>
                    <Text style={s.secondaryBtnText}>{assignee ? t('visitWorkspace.assigneeChosen') : t('visits.execute.selectAssignee')}</Text>
                  </TouchableOpacity>
                </View>
              )}
              {outcome && (
                <TextInput
                  style={s.input}
                  value={outcomeNote}
                  onChangeText={setOutcomeNote}
                  placeholder={outcome.action?.type === 'note' ? t('visits.execute.outcomeNote') : t('visitWorkspace.outcomeNoteOptional')}
                  placeholderTextColor={colors.text.disabled as string}
                  multiline
                />
              )}
              {photos?.requireOnEnd && (
                <>
                  <Text style={s.sectionLabel}>{t('visits.execute.endPhotos')}</Text>
                  {photoStrip(endPhotos, 'end')}
                </>
              )}
              <View style={s.endButtons}>
                <TouchableOpacity style={s.secondaryBtn} onPress={() => { setShowOutcomes(false); setOutcome(null); }}>
                  <Text style={s.secondaryBtnText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[s.primaryBtn, { flex: 1, marginTop: 0 }, (!outcome || endNeedsPhotos || busy === 'end') && s.disabled]}
                  disabled={!outcome || endNeedsPhotos || busy === 'end'}
                  onPress={handleEnd}
                >
                  {busy === 'end' ? <ActivityIndicator color={colors.text.onPrimary as string} /> : <Ionicons name="checkmark-circle" size={18} color={colors.text.onPrimary as string} />}
                  <Text style={s.primaryBtnText}>{t('visitWorkspace.confirmEnd')}</Text>
                </TouchableOpacity>
              </View>
              {endNeedsPhotos && <Text style={s.hint}>{t('visitWorkspace.needEndPhotos')}</Text>}
            </View>
          )}
        </View>
      )}

      {/* What came out of this visit */}
      {(visit.activities?.length ?? 0) > 0 && (
        <View style={s.rules}>
          <Text style={s.sectionLabel}>{t('visitWorkspace.activitiesTitle')}</Text>
          {visit.activities!.map((a) => (
            <View key={a.id} style={s.ruleRow}>
              <Ionicons name={ACTIVITY_ICON[a.type] || 'ellipse-outline'} size={14} color={colors.primary as string} />
              <Text style={s.ruleText} numberOfLines={1}>
                {t(`visitWorkspace.activity.${a.type}` as any)}{a.label ? ` · ${a.label}` : ''}
              </Text>
              <Text style={s.muted}>{formatDateTime(a.at)}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Finished visit — summary */}
      {!isStartable && !isActive && visit.outcome && (
        <Text style={s.muted}>{t('visits.detail.outcome')}: {visit.outcome.label}</Text>
      )}

      <Modal visible={mapOpen} animationType="fade" onRequestClose={() => setMapOpen(false)}>
        {hasTarget && (
          <View style={{ flex: 1 }}>
            <LiveVisitMap
              agentPosition={(liveDirections.position || position) as LiveVisitMapPosition | null}
              destination={{ latitude: target!.latitude!, longitude: target!.longitude!, address: target?.address }}
              geometry={liveDirections.geometry}
              travelMode="driving"
              isFullscreen
              onClose={() => setMapOpen(false)}
            />
          </View>
        )}
      </Modal>

      <BusinessUserPicker
        visible={showAssigneePicker}
        onClose={() => setShowAssigneePicker(false)}
        onConfirm={(ids) => { setAssignee(ids[0]); setShowAssigneePicker(false); }}
        mode="single"
        source={assignSource}
        title={t('visits.execute.selectAssignee')}
      />
    </View>
  );
};

const getStyles = () =>
  StyleSheet.create({
    card: {
      borderWidth: 1.5,
      borderColor: colors.primary,
      borderRadius: spacing.borderRadius.md,
      backgroundColor: colors.background.surface,
      padding: spacing.md,
      marginBottom: spacing.md,
      gap: spacing.sm,
    },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    statusDot: { width: 10, height: 10, borderRadius: 5 },
    title: { fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
    subtitle: { fontSize: 12, fontFamily: typography.fontFamily.primary, color: colors.text.secondary, marginTop: 2 },
    iconBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.secondary },
    directionsRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    secondaryBtn: {
      flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
      paddingHorizontal: 12, height: 38, borderRadius: 10, borderWidth: 1, borderColor: colors.primary, backgroundColor: colors.primaryTransparent,
    },
    secondaryBtnText: { fontSize: 13, fontFamily: typography.fontFamily.semibold, color: colors.primary },
    muted: { fontSize: 12, lineHeight: 17, fontFamily: typography.fontFamily.primary, color: colors.text.secondary },
    link: { fontSize: 12, fontFamily: typography.fontFamily.semibold, color: colors.primary },
    rules: { borderRadius: 10, backgroundColor: colors.background.secondary, padding: spacing.sm, gap: 4 },
    sectionLabel: { fontSize: 12, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary, marginBottom: 2 },
    ruleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    ruleText: { flex: 1, fontSize: 13, fontFamily: typography.fontFamily.primary, color: colors.text.primary },
    block: { gap: spacing.sm },
    warn: { fontSize: 12, lineHeight: 17, fontFamily: typography.fontFamily.medium, color: colors.warning },
    hint: { fontSize: 12, fontFamily: typography.fontFamily.primary, color: colors.text.secondary, textAlign: 'center' },
    input: {
      minHeight: 42, borderRadius: 10, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.secondary,
      paddingHorizontal: 12, paddingVertical: 9, fontSize: 14, fontFamily: typography.fontFamily.primary, color: colors.text.primary,
      ...Platform.select({ web: { outlineStyle: 'none' } as any }),
    } as any,
    primaryBtn: {
      flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8,
      height: 48, borderRadius: 12, backgroundColor: colors.primary, marginTop: 4,
    },
    primaryBtnText: { fontSize: 15, fontFamily: typography.fontFamily.bold, color: colors.text.onPrimary },
    disabled: { opacity: 0.45 },
    photoRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    photo: { width: 60, height: 60, borderRadius: 8, backgroundColor: colors.background.secondary },
    photoRemove: { position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10, backgroundColor: colors.error, alignItems: 'center', justifyContent: 'center' },
    photoAdd: { width: 60, height: 60, borderRadius: 8, borderWidth: 1.5, borderStyle: 'dashed', borderColor: colors.primary, alignItems: 'center', justifyContent: 'center' },
    actionGrid: { flexDirection: 'row', gap: spacing.sm },
    actionTile: {
      flex: 1, alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: 10, borderRadius: 10,
      borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.secondary,
    },
    actionTileText: { fontSize: 11, fontFamily: typography.fontFamily.medium, color: colors.text.primary, textAlign: 'center' },
    noteRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8 },
    noteBtn: { width: 42, height: 42, borderRadius: 10, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' },
    endSheet: { gap: spacing.sm, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border, paddingTop: spacing.sm },
    outcomeWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    outcomeChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 18, borderWidth: 1.5, borderColor: colors.border },
    outcomeText: { fontSize: 13, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
    assignRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
    segment: { paddingHorizontal: 12, height: 34, borderRadius: 17, borderWidth: 1, borderColor: colors.border, justifyContent: 'center' },
    segmentOn: { borderColor: colors.primary, backgroundColor: colors.primaryTransparent },
    segmentText: { fontSize: 12, fontFamily: typography.fontFamily.medium, color: colors.text.secondary },
    segmentTextOn: { color: colors.primary },
    endButtons: { flexDirection: 'row', gap: spacing.sm, alignItems: 'center' },
  });
